<script lang="ts" setup>
import { capitalize, snakeCase, unslugify } from '@thecompaniesapi/string-helpers'
import { useDebounceFn, useIntervalFn } from '@vueuse/core'

const props = defineProps({
  action: {
    default: undefined,
    type: Object as PropType<Action>,
  },
  bodyClasses: {
    default: '',
    type: String,
  },
  disableContentBlocker: {
    default: false,
    type: Boolean,
  },
  disableSorting: {
    default: false,
    type: Boolean,
  },
  feature: {
    required: true,
    type: String as PropType<SegmentationFeature>,
  },
  freeRequestFill: {
    default: true,
    type: Boolean,
  },
  hasPagination: {
    default: true,
    type: Boolean,
  },
  hasSelection: {
    default: false,
    type: Boolean,
  },
  list: {
    default: undefined,
    type: Object as PropType<List>,
  },
  responseToOverride: {
    default: undefined,
    type: Object as PropType<{
      companies: CompanyV2[]
      count?: number
      currentPage?: number
      freeRequest?: boolean
      hasQuery?: boolean
      lastPage?: number
      perPage?: number
    }>,
  },
  scrollTopOnPageChange: {
    default: true,
    type: Boolean,
  },
  selectionStacked: {
    default: false,
    type: Boolean,
  },
  size: {
    default: 25,
    type: Number,
  },
  view: {
    default: 'search',
    type: String as PropType<CompaniesTableView>,
  },
  wrapperClasses: {
    default: '',
    type: String,
  },
})

const { checkIfQueriesMatchWithEachOther, checkIfSimilarQueriesMatchWithEachOther, getQueryFromUrl, getSimilarQueryFromUrl } = useSegmentation()
const { createEvent } = useAnalytics()
const { sortFields } = useSegmentationSort()
const { t } = useI18n()
const { toggleCompaniesFromList, togglingFromList } = useListActions()
const { showCompanyProfile } = useCompany()
const billingStore = useBillingStore()
const companiesStore = useCompaniesStore()
const guards = useGuards()
const localePath = useLocalePath()
const route = useRoute()
const router = useRouter()
const userStore = useUserStore()
const { isOverlayOpened, toggleModal } = useOverlays()

const id = useId()
const viewId = computed(() => {
  return `${props?.view}-${props?.feature}-${id}`
})

const currentPage = useRouteQuery<number>('page', 1)
const isWaitingQuery = useRouteQuery<'false' | 'true'>('wait-companies', 'false')
const hasQuery = useState<boolean>(`companies-table-has-query-${viewId.value}`, () => false)
const nextPage = useState<number>(`companies-table-next-page-${viewId.value}`, () => 1)
const companies = useState<CompanyV2[]>(`companies-table-companies-${viewId.value}`, () => [])
const count = useState<number>(`companies-table-count-${viewId.value}`, () => 0)
const perPage = useState<number>(`companies-table-per-page-${viewId.value}`, () => props.size)
const freeRequest = useState<boolean>(`companies-table-free-request-${viewId.value}`, () => false)
const lastPage = useState<number>(`companies-table-last-page-${viewId.value}`, () => 1)
const currentQuery = useState<SegmentationCondition[]>(`companies-table-current-query-${viewId.value}`, () => getQueryFromUrl())
const lastCompaniesSize = useState<number>(`companies-table-last-companies-size-${viewId.value}`, () => props.size)
const loading = useState<boolean>(`companies-table-loading-${viewId.value}`, () => false)
const selectedColumns = useState<string[]>(`companies-table-selected-columns-${viewId.value}`, () => [])
const selection = useState<number[]>(`companies-table-selection-${viewId.value}`, () => [])
const similarDomains = useState<string[]>(`companies-table-similar-domains-${viewId.value}`, () => [])

const isWaiting = computed({
  get: () => isWaitingQuery.value === 'true',
  set: (value: boolean) => (isWaitingQuery.value = value ? 'true' : 'false'),
})

// Refresh every second if waiting for companies
const { pause } = useIntervalFn(
  async () => {
    if (isWaiting.value) {
      await fetchCompanies()

      return
    }

    // If not waiting anymore, stop this interval
    pause()
  },
  1000,
  { immediate: isWaiting.value },
)

const columnsToDisplay = computed(() => {
  const columns = {
    enrichment: [],
    search: [
      {
        icon: getIcon('logo'),
        label: t('datapoints.attributes.assets-logoSquare'),
        value: 'logo',
      },
      {
        icon: getIcon('name'),
        label: t('datapoints.attributes.about-name'),
        value: 'nameAndDomain',
      },
      {
        icon: getIcon('industries'),
        label: t('datapoints.attributes.about-industries'),
        value: 'industries',
      },
      {
        icon: getIcon('totalEmployees'),
        label: t('datapoints.attributes.about-totalEmployees'),
        value: 'totalEmployees',
      },
      {
        icon: getIcon('revenue'),
        label: t('datapoints.attributes.finances-revenue'),
        value: 'revenue',
      },
      {
        icon: getIcon('businessType'),
        label: t('datapoints.attributes.about-businessType'),
        value: 'businessType',
      },
      {
        icon: getIcon('monthlyVisitors'),
        label: t('datapoints.attributes.analytics-monthlyVisitors'),
        value: 'monthlyVisitors',
      },
      {
        icon: getIcon('country'),
        label: t('datapoints.attributes.locations-headquarters-country'),
        value: 'country',
      },
      {
        icon: getIcon('city'),
        label: t('datapoints.attributes.locations-headquarters-city'),
        value: 'city',
      },
      {
        icon: getIcon('yearFounded'),
        label: t('datapoints.attributes.about-yearFounded'),
        value: 'yearFounded',
      },
      {
        icon: getIcon('socialNetworks'),
        label: t('datapoints.attributes.socials'),
        value: 'socialNetworks',
      },
      {
        icon: getIcon('technologies'),
        label: t('datapoints.attributes.technologies'),
        value: 'technologies',
      },
      {
        icon: getIcon('apps'),
        label: t('datapoints.attributes.apps'),
        value: 'apps',
      },
      {
        icon: getIcon('lists'),
        label: t('datapoints.attributes.meta-listIds'),
        value: 'lists',
      },
      {
        icon: getIcon('refresh'),
        label: t('datapoints.attributes.meta-syncedAt'),
        value: 'syncedAt',
      },
      {
        icon: getIcon('score'),
        label: t('datapoints.attributes.meta-score'),
        value: 'score',
      },
      {
        icon: getIcon('similarity'),
        label: t('datapoints.attributes.meta-similarity'),
        value: 'similarity',
      },
    ],
    similar: [
      {
        icon: getIcon('logo'),
        label: t('datapoints.attributes.assets-logoSquare'),
        value: 'logos',
      },
      {
        icon: getIcon('similar'),
        label: t('datapoints.attributes.meta-similarAttributes'),
        value: 'similarAttributes',
      },
      {
        icon: getIcon('similarity'),
        label: t('datapoints.attributes.meta-similarity'),
        value: 'similarity',
      },
    ],
  }

  return columns
})

const columnsToDisplayInTable = computed(() => {
  const columns: UColumn[] = []

  if (!loading.value && (!companies.value.length || (props.feature === 'similar' && !similarDomains.value.length))) {
    return []
  }

  if (props.hasSelection) {
    columns.push({ key: 'selection' })
  }

  if (props.action && props.action.data?.fields) {
    columns.push({ key: 'logo' })

    columns.push({
      icon: getIcon('name'),
      key: 'nameAndDomain',
      label: t('datapoints.attributes.about-name'),
    })

    for (const field of props.action.data.fields) {
      columns.push({
        icon: getIcon('ask'),
        key: field.key,
        label: capitalize(unslugify(snakeCase(field.key)), true),
      })
    }

    columns.push({
      icon: getIcon('score'),
      key: 'answerScore',
      label: t('actions.answerScore'),
    })

    columns.push({
      icon: getIcon('score'),
      key: 'explanation',
      label: t('actions.explanation'),
    })

    return columns
  }

  // Columns for similar companies
  if (props.feature === 'similar') {
    if (selectedColumns.value.includes('logos')) {
      columns.push({ key: 'logos' })
    }

    if (selectedColumns.value.includes('similarAttributes')) {
      columns.push({
        icon: getIcon('similar'),
        key: 'similarAttributes',
        label: t('datapoints.attributes.meta-similarAttributes'),
      })
    }

    if (selectedColumns.value.includes('similarity')) {
      columns.push({
        icon: getIcon('similarity'),
        key: 'similarity',
        label: t('datapoints.attributes.meta-similarity'),
      })
    }
  }
  // Columns for search companies
  else if (props.feature === 'search' || props.feature === 'list') {
    if (selectedColumns.value.includes('logo')) {
      columns.push({ key: 'logo' })
    }

    if (selectedColumns.value.includes('nameAndDomain')) {
      columns.push({
        icon: getIcon('name'),
        key: 'nameAndDomain',
        label: t('datapoints.attributes.about-name'),
        sortKey: 'about.name',
      })
    }

    if (selectedColumns.value.includes('industries')) {
      columns.push({
        icon: getIcon('industries'),
        key: 'industries',
        label: t('datapoints.attributes.about-industries'),
        sortKey: 'about.industries',
      })
    }

    if (selectedColumns.value.includes('totalEmployees')) {
      columns.push({
        icon: getIcon('totalEmployees'),
        key: 'totalEmployees',
        label: t('datapoints.attributes.about-totalEmployees'),
        sortKey: 'about.totalEmployees',
      })
    }

    if (selectedColumns.value.includes('revenue')) {
      columns.push({
        icon: getIcon('revenue'),
        key: 'revenue',
        label: t('datapoints.attributes.finances-revenue'),
        sortKey: 'finances.revenue',
      })
    }

    if (selectedColumns.value.includes('businessType')) {
      columns.push({
        icon: getIcon('businessType'),
        key: 'businessType',
        label: t('datapoints.attributes.about-businessType'),
        sortKey: 'about.businessType',
      })
    }

    if (selectedColumns.value.includes('monthlyVisitors')) {
      columns.push({
        icon: getIcon('monthlyVisitors'),
        key: 'monthlyVisitors',
        label: t('datapoints.attributes.analytics-monthlyVisitors'),
        sortKey: 'analytics.monthlyVisitors',
      })
    }

    if (selectedColumns.value.includes('country')) {
      columns.push({
        icon: getIcon('country'),
        key: 'country',
        label: t('datapoints.attributes.locations-headquarters-country'),
        sortKey: 'locations.headquarters.country.code',
      })
    }

    if (selectedColumns.value.includes('city')) {
      columns.push({
        icon: getIcon('city'),
        key: 'city',
        label: t('datapoints.attributes.locations-headquarters-city'),
        sortKey: 'locations.headquarters.city.code',
      })
    }

    if (selectedColumns.value.includes('yearFounded')) {
      columns.push({
        icon: getIcon('yearFounded'),
        key: 'yearFounded',
        label: t('datapoints.attributes.about-yearFounded'),
        sortKey: 'about.yearFounded',
      })
    }

    if (selectedColumns.value.includes('socialNetworks')) {
      columns.push({
        icon: getIcon('socialNetworks'),
        key: 'socialNetworks',
        label: t('datapoints.attributes.socials'),
        sortKey: 'socials',
      })
    }

    if (selectedColumns.value.includes('technologies')) {
      columns.push({
        icon: getIcon('technologies'),
        key: 'technologies',
        label: t('datapoints.attributes.technologies'),
        sortKey: 'technologies.active',
      })
    }

    if (selectedColumns.value.includes('apps')) {
      columns.push({
        icon: getIcon('apps'),
        key: 'apps',
        label: t('datapoints.attributes.apps'),
        sortKey: 'apps',
      })
    }

    if (selectedColumns.value.includes('lists')) {
      columns.push({
        icon: getIcon('lists'),
        key: 'lists',
        label: t('datapoints.attributes.meta-listIds'),
      })
    }

    if (selectedColumns.value.includes('score')) {
      columns.push({
        icon: getIcon('score'),
        key: 'score',
        label: t('datapoints.attributes.meta-score'),
        sortKey: 'meta.score',
      })
    }

    if (
      selectedColumns.value.includes('similarity')
      && currentQuery.value.find(query => query.attribute === 'ai.search')
    ) {
      columns.push({
        icon: getIcon('similarity'),
        key: 'similarity',
        label: t('datapoints.attributes.meta-similarity'),
      })
    }

    if (selectedColumns.value.includes('syncedAt')) {
      columns.push({
        icon: getIcon('refresh'),
        key: 'syncedAt',
        label: t('datapoints.attributes.meta-syncedAt'),
        sortKey: 'meta.syncedAt',
      })
    }
  }

  return columns
})

const hasAllSelected = computed(() => {
  let result = true

  for (const company of companies.value) {
    if (!company?.meta?.mysqlId) {
      continue
    }

    if (!selection.value.includes(company.meta!.mysqlId!)) {
      result = false
      break
    }
  }

  return result ? ['all'] : false
})

const isContentBlocked = computed(() => {
  return (!userStore.user || (currentPage.value > 1 && userStore.userType === 'user'))
    && !props.disableContentBlocker
    && !props.list
})

const selectedCompanies = computed(() => {
  return companies.value.filter((company: any) => {
    return company.meta?.mysqlId && selection.value.includes(company.meta.mysqlId)
  })
})

watch(
  () => route.query,
  (newQuery, oldQuery) => {
    // For each feature, check if the queries match
    let matched = true

    if (props.feature === 'search' || props.feature === 'list') {
      matched = checkIfQueriesMatchWithEachOther({
        query1: getQueryFromUrl(oldQuery),
        query2: getQueryFromUrl(newQuery),
      })
    }
    else if (props.feature === 'similar') {
      matched = checkIfSimilarQueriesMatchWithEachOther({
        domains1: getSimilarQueryFromUrl(newQuery),
        domains2: getSimilarQueryFromUrl(oldQuery),
      })
    }

    if (
      !matched
      || !companies.value.length
      || newQuery.page !== oldQuery?.page
      || newQuery.sortFields !== oldQuery?.sortFields
      || newQuery.action !== oldQuery?.action
    ) {
      currentPage.value = route.query?.page ? Number.parseInt(`${route.query.page}`) : 1
      currentQuery.value = getQueryFromUrl(newQuery)
    }
  },
  { immediate: true },
)

// Update on query change or when user is logged in
watch(
  [
    currentQuery,
    () => userStore.user,
    () => props.action,
  ],
  fetchCompanies,
)

watch(
  () => props.responseToOverride,
  (newResponseToOverride) => {
    if (newResponseToOverride) {
      companies.value = newResponseToOverride.companies || []
      count.value = newResponseToOverride.count || 0
      freeRequest.value = newResponseToOverride.freeRequest || false
      hasQuery.value = newResponseToOverride.hasQuery || false
      perPage.value = newResponseToOverride.perPage || props.size
      currentPage.value = newResponseToOverride.currentPage || 1
      lastPage.value = newResponseToOverride.lastPage || 1
      nextPage.value = (newResponseToOverride?.currentPage || 1) + 1 > (newResponseToOverride?.lastPage || 1)
        ? 1
        : (newResponseToOverride?.currentPage || 1) + 1
    }
  },
  { immediate: true },
)

onMounted(() => {
  if (import.meta.client && ['search'].includes(props.feature)) {
    // Trigger subscription modal when user is not subscriber and page is above 1
    if (currentPage.value > 1 && !props.list && !billingStore.isSubscriptionActive) {
      toggleModal({
        data: { totalCompanies: count.value },
        opened: true,
        title: 'pageOneOnly',
        type: 'subscription',
      })
    }
  }
})

async function fetchCompanies() {
  loading.value = true

  try {
    // Skip if overriden response is set
    if (props.responseToOverride) {
      isWaiting.value = false

      return
    }

    // Fetch companies for a specific list
    if (props.list) {
      count.value = await companiesStore.fetchCount({
        actionId: props?.action?.id,
        query: [
          {
            attribute: 'meta.listIds',
            operator: 'and',
            sign: 'equals',
            values: [props.list.id],
          },
        ],
        setTotal: true,
      })

      const response = await companiesStore.fetchCompanies({
        actionId: props?.action?.id,
        listId: props.list.id,
        page: currentPage.value,
        query: getQueryFromUrl(),
        size: props.size,
        sortFields: sortFields.value,
        sortKey: route?.query.sortKey as CompaniesSortKey,
        sortOrder: route?.query.sortOrder as CompaniesSortOrder,
      })

      if (response) {
        companies.value = response.companies
        freeRequest.value = response.freeRequest
        hasQuery.value = response.hasQuery
        perPage.value = response.perPage
        nextPage.value = response.nextPage
        lastPage.value = response.lastPage
        hasQuery.value = response.hasQuery
        perPage.value = response.perPage
        count.value = response.total
      }
    }
    // Fetch similar companies for a list of domains
    else if (props.feature === 'similar') {
      similarDomains.value = route?.query.domains ? `${route?.query.domains}`.split(',') : []

      if (similarDomains.value.length) {
        const response = await companiesStore.fetchCompanies({
          page: currentPage.value,
          similarDomains: similarDomains.value,
          size: props.size,
        })

        if (response) {
          companies.value = response.companies
          freeRequest.value = response.freeRequest
          hasQuery.value = response.hasQuery
          perPage.value = response.perPage
          lastPage.value = response.lastPage
          nextPage.value = response.nextPage
          hasQuery.value = response.hasQuery
          perPage.value = response.perPage
          count.value = response.total
        }

        await createEvent({
          name: '[Companies] Search Companies',
          properties: {
            Feature: 'similar',
            SimilarDomains: similarDomains.value,
          },
        })
      }
      else {
        resetTable()
      }
    }
    // Fetch companies from query
    else {
      const query = getQueryFromUrl()

      const [countResponse, companiesResponse] = await Promise.all([
        companiesStore.fetchCount({
          actionId: props?.action?.id,
          query,
        }),

        companiesStore.fetchCompanies({
          actionId: props?.action?.id,
          page: isContentBlocked.value ? 1 : currentPage.value,
          query,
          size: props.size,
          sortFields: sortFields.value,
          sortKey: route?.query.sortKey as CompaniesSortKey,
          sortOrder: route?.query.sortOrder as CompaniesSortOrder,
        }),
      ])

      if (companiesResponse) {
        companies.value = companiesResponse.companies
        freeRequest.value = companiesResponse.freeRequest
        hasQuery.value = companiesResponse.hasQuery
        perPage.value = companiesResponse.perPage
        nextPage.value = companiesResponse.nextPage
        lastPage.value = companiesResponse.lastPage
        count.value = companiesResponse.total
      }

      if (countResponse) {
        count.value = countResponse
      }

      // Log event
      if (query.length) {
        await createEvent({
          name: '[Companies] Search Companies',
          properties: {
            Attributes: query
              .filter(condition => condition.values.length)
              .map(condition => condition.attribute),
            Feature: 'search',
          },
        })
      }
    }
  }
  catch (error) {
    const axiosError = error as AxiosError
    const axiosErrorData = axiosError.response?.data as { message?: string }

    switch (axiosErrorData?.message) {
      case 'userCurrentTeamIsNotInstanceOwner':
        router.push(localePath('index'))
        break

      default:
        console.error(error)
    }
  }
  finally {
    lastCompaniesSize.value = companies.value?.length || props.size
    loading.value = false

    if (companies.value.length > 0) {
      isWaiting.value = false
    }
  }
}

function generateSelectedColumns() {
  let targetFeature = props.feature

  if (targetFeature === 'list') {
    targetFeature = 'search'
  }

  for (const column of columnsToDisplay?.value?.[targetFeature as 'search' | 'similar']) {
    selectedColumns.value.push(column.value)
  }
}

async function onRemoveSelectedCompanies() {
  if (!props.list) {
    selection.value = []
  }

  if (guards.userOnly() && props.list) {
    try {
      await toggleCompaniesFromList({
        action: 'detach',
        companies: selectedCompanies.value,
        list: props.list,
        onToggleCompanies: ({ action, companies: updatedCompanies }) => {
          if (props.list) {
            companies.value = companiesStore.updateListIdsForCompanies({
              action,
              companiesIds: updatedCompanies.filter(company => company.meta?.mysqlId).map(company => company.meta!.mysqlId!),
              currentCompanies: companies.value,
              listId: props.list.id,
            })
          }
        },
      })

      selection.value = []

      smoothScrollTo({
        axis: 'y',
        target: 0,
      })

      await fetchCompanies()
    }
    catch (error) {
      console.error(error)
    }
  }
}

function resetTable() {
  companies.value = []
  count.value = 0
  hasQuery.value = false
  perPage.value = props.size
}

function onShowCompanyProfile(domain?: string) {
  showCompanyProfile(domain)
}

function onPageChange(page: number) {
  if (loading.value) {
    return
  }

  if (guards.userOnly({ title: 'moreResults' })) {
    // Prevent to go beyond the first and last page
    if (page < 1 || page > Math.ceil(count.value / perPage.value)) {
      return
    }

    // Only subscriber can access pages above 1
    if (
      page > 1
      && !props.list
      && !guards.subscriberOnly({
        data: { totalCompanies: count.value },
        popup: true,
        popupTitle: 'pageOneOnly',
      })
    ) {
      currentPage.value = 1

      return
    }

    if (props.scrollTopOnPageChange) {
      smoothScrollTo({
        axis: 'y',
        target: 0,
      })
    }

    currentPage.value = page

    // Updating the route will fetch the current page automatically
    router.push({
      name: String(route.name),
      params: { ...route?.params },
      query: {
        ...route?.query,
        page: currentPage.value,
      },
    })
  }
  else {
    currentPage.value = 1
  }
}

function onToggleSelectAll() {
  // Only drop companies from current page
  if (hasAllSelected.value) {
    const currentPageIds = companies.value.map(company => company.meta!.mysqlId!)

    selection.value = selection.value.filter(id => !currentPageIds.includes(id))

    return
  }

  // Merge all current page companies with selection and avoid duplicates using Set
  selection.value = Array.from(new Set([
    ...companies.value.map((company) => {
      return company.meta!.mysqlId!
    }),
    ...selection.value,
  ].filter(Boolean)))
}

function onToggleCompaniesFromList(params: {
  action: 'attach' | 'detach'
  companies: CompanyV2[]
  listId: number
}) {
  companies.value = companiesStore.updateListIdsForCompanies({
    action: params.action,
    companiesIds: params.companies.map(company => company.meta!.mysqlId!),
    currentCompanies: companies.value,
    listId: params.listId,
  })
}

defineExpose({
  fetchCompanies,
  page: computed(() => {
    return currentPage.value
  }),
  query: computed(() => {
    return getQueryFromUrl()
  }),
  similarDomains,
  size: computed(() => {
    return props.size
  }),
  sortFields,
  sortKey: computed(() => {
    return route?.query.sortKey as string
  }),
  sortOrder: computed(() => {
    return route?.query.sortOrder as ElasticSortOrder
  }),
})

defineShortcuts({
  arrowleft: useDebounceFn(
    () => {
      if (isOverlayOpened.value) {
        return
      }

      onPageChange(currentPage.value - 1)
    },
    100,
  ),
  arrowRight: useDebounceFn(
    () => {
      if (isOverlayOpened.value) {
        return
      }

      onPageChange(currentPage.value + 1)
    },
    100,
  ),
})

const listChannel = computed(() => {
  if (!props?.list) {
    return
  }

  return `lists/${props?.list?.id}` as `lists/${string}`
})

// List events: attach companies
useEvent(
  listChannel,
  'list:attach-companies',
  async ({ companies: eventCompanies, total }) => {
    companies.value = await companiesStore.addOrReplaceCompanies({
      currentCompanies: companies.value,
      newCompanies: eventCompanies as CompanyV2[],
      perPage: props.size,
    })

    if (companies.value.length > 0) {
      isWaiting.value = false
    }

    // Update total from socket if available
    if (total) {
      count.value = total
    }
  },
)

// List events: detach companies
useEvent(
  listChannel,
  'list:detach-companies',
  async ({ ids, total }) => {
    // Remove companies from list
    companies.value = await companiesStore.removeCompanies({
      existingCompanies: companies.value,
      mysqlIds: ids as number[],
    })

    // If the list is empty, fetch companies
    if (companies.value.length === 0) {
      const companiesResponse = await companiesStore.fetchCompanies({
        listId: props?.list?.id,
        page: currentPage.value,
        query: getQueryFromUrl(),
        size: props.size,
        sortFields: sortFields.value,
        sortKey: route?.query.sortKey as CompaniesSortKey,
        sortOrder: route?.query.sortOrder as CompaniesSortOrder,
      })

      if (companiesResponse) {
        companies.value = companiesResponse.companies
        freeRequest.value = companiesResponse.freeRequest
        hasQuery.value = companiesResponse.hasQuery
        perPage.value = companiesResponse.perPage
        nextPage.value = companiesResponse.nextPage
        lastPage.value = companiesResponse.lastPage
        count.value = companiesResponse.total
      }
    }

    if (companies.value.length > 0) {
      isWaiting.value = false
    }

    // Update total from socket if available
    if (total) {
      count.value = total
    }
  },
)

useEvent(
  'team',
  'actions:results',
  async ({ actionId, companies: eventCompanies }) => {
    if (actionId && eventCompanies?.length) {
      companies.value = await companiesStore.addOrReplaceCompanies({
        actionId,
        currentCompanies: companies.value,
        newCompanies: eventCompanies,
        perPage: props.size,
      })
    }
  },
)

generateSelectedColumns()

await callOnce(
  `companies-table-companies-table-${viewId.value}`,
  fetchCompanies,
  { mode: 'navigation' },
)
</script>

<template>
  <div class="flex h-full flex-col">
    <div
      v-if="$slots.header"
      class="mb-4 grid w-full grid-cols-1 gap-4 md:grid-cols-[1fr,auto] md:gap-2"
    >
      <div
        id="left"
        class="order-1 min-w-0"
      >
        <slot
          :companies="companies"
          :count="count"
          name="header"
        />
      </div>

      <div
        id="right"
        class="order-2 flex w-auto flex-row"
        :class="{
          'flex-col items-start justify-end gap-4 md:flex-col md:items-end md:justify-end': selectionStacked,
          'flex-col gap-4 md:flex-row md:items-end md:justify-end': !selectionStacked,
        }"
      >
        <div
          v-if="selection.length || action"
          class="relative flex items-center gap-2"
        >
          <CompaniesTableSelection
            v-if="selection.length"
            :feature="feature"
            :list="list"
            :selection="selection"
            :toggling-from-list="togglingFromList"
            @remove-selected-companies-from-list="onRemoveSelectedCompanies"
            @toggle-companies-from-list="onToggleCompaniesFromList"
          />

          <CompaniesTableAction
            v-if="action"
            :action="action"
            :list="list"
          />
        </div>

        <CompaniesCount
          v-if="view === 'similar' ? similarDomains.length : true"
          class="w-fit"
          :companies-per-page="size"
          :current-page="currentPage"
          :loading="loading"
          :total-companies="count"
        />
      </div>
    </div>

    <div class="relative size-full">
      <CompaniesTableBody
        v-model="selection"
        :action="action"
        :class="bodyClasses"
        :columns-to-display-in-table="columnsToDisplayInTable"
        :companies="companies"
        :companies-per-page="size"
        :count="count"
        :current-page="currentPage"
        :disable-sorting="disableSorting"
        :feature="feature"
        :free-request="freeRequest"
        :free-request-fill="freeRequestFill"
        :has-all-selected="hasAllSelected"
        :is-waiting="isWaiting"
        :last-companies-size="lastCompaniesSize"
        :loading="loading"
        :similar-domains="similarDomains"
        :sort-fields="sortFields"
        :wrapper-classes="wrapperClasses"
        @open-profile="onShowCompanyProfile"
        @select-all="onToggleSelectAll"
      />

      <div
        v-if="freeRequest && hasQuery && freeRequestFill && !userStore?.user?.emailVerified && companies.length"
        class="absolute bottom-0 left-0 flex h-[85%] w-full items-center justify-center overflow-hidden"
      >
        <div class="z-20">
          <CompaniesSimplifiedPlaceholder more-results />
        </div>

        <div class="absolute left-0 top-0 size-full overflow-hidden rounded-lg backdrop-blur-lg">
          <BaseMask section />
        </div>
      </div>
    </div>

    <CompaniesTablePagination
      v-if="hasPagination && (view === 'similar' ? similarDomains.length : true)"
      v-model="currentPage"
      :count="count"
      :per-page="size"
      @page-change="onPageChange"
    />
  </div>
</template>
